import { lifecyclePayload, publicEntranceLifecycle, type EntranceLifecycle } from './entranceLifecycle';

interface EnableEntranceIO {
  read: (id: string, signal?: AbortSignal) => Promise<EntranceLifecycle>;
  enable: (view: EntranceLifecycle) => Promise<EntranceLifecycle>;
}

// Reuse lifecycle readiness and CAS; source previews never authorize activation.
export async function checkAndEnableEntrance(
  entranceId: string, confirmed: boolean, lock: { current: boolean },
  isCurrent: () => boolean, io: EnableEntranceIO, signal?: AbortSignal
): Promise<{ view: EntranceLifecycle; enabled: boolean } | null> {
  const current = () => isCurrent() && !signal?.aborted;
  if (!confirmed || lock.current || !current()) return null;
  lock.current = true;
  try {
    const view = publicEntranceLifecycle(await io.read(entranceId, signal));
    if (!current()) return null;
    if (view.entranceId !== entranceId) throw new Error('Unexpected entrance');
    if (view.state === 'active') return { view, enabled: true };
    if (view.state === 'deleted' || view.canEnable !== true) return { view, enabled: false };
    lifecyclePayload(view, 'enable', '', false, true);
    const next = publicEntranceLifecycle(await io.enable(view));
    if (!current()) return null;
    if (next.entranceId !== entranceId || next.state !== 'active' || next.revision <= view.revision ||
        next.emergencyEpoch !== view.emergencyEpoch) throw new Error('Unexpected activation response');
    return { view: next, enabled: true };
  } finally {
    lock.current = false;
  }
}
